import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleRow from '@/components/feature/ArticleRow';
import Pagination from '@/components/feature/Pagination';
import { getCategoryById } from '@/mocks/news';
import { filterByTime, getByCategory, sortArticles, type SortKey, type TimeRange } from '@/utils/news';
import { useSEO } from '@/hooks/useSEO';

const PAGE_SIZE = 4;

const timeOptions: { key: TimeRange; label: string }[] = [
  { key: 'all', label: '全部时间' },
  { key: '24h', label: '最近24小时' },
  { key: '7d', label: '最近7天' },
];

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新发布' },
  { key: 'hot', label: '最热门' },
];

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = getCategoryById(categoryId);

  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  useSEO({
    title: category ? `${category.name}新闻 - 911爆料网` : '新闻分类 - 911爆料网',
    description: category
      ? `${category.desc} 911爆料网${category.name}栏目为你汇总最新相关报道。`
      : '911爆料网新闻分类页，按栏目浏览社会热点、突发新闻与民生资讯。',
    keywords: category
      ? `${category.name},${category.name}新闻,911爆料网,爆料新闻`
      : '新闻分类,911爆料网,社会热点,突发新闻',
  });

  const filtered = useMemo(() => {
    const base = getByCategory(categoryId);
    return sortArticles(filterByTime(base, timeRange), sortKey);
  }, [categoryId, timeRange, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setTimeRange('all');
    setSortKey('latest');
    setPage(1);
  };

  if (!category) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            栏目不存在
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            你访问的栏目可能已被调整或尚未开放，不妨返回首页看看最新的爆料。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.name }]} />

        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:justify-between md:p-6">
          <div>
            <h1 className="flex items-center gap-2.5 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-background-50">
                <i className={`${category.icon} text-2xl`}></i>
              </span>
              {category.name}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {category.desc}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-file-list-3-line text-primary-600"></i>
            共 {getByCategory(categoryId).length} 篇报道
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 pl-1 text-sm text-foreground-500">
              <i className="ri-time-line"></i>
              时间
            </span>
            {timeOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setTimeRange(opt.key);
                  setPage(1);
                }}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  timeRange === opt.key
                    ? 'bg-primary-600 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 pl-1 text-sm text-foreground-500">
              <i className="ri-sort-desc"></i>
              排序
            </span>
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setSortKey(opt.key);
                  setPage(1);
                }}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  sortKey === opt.key
                    ? 'bg-primary-600 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
            {pageItems.map((article) => (
              <ArticleRow key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-2-line text-3xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              当前筛选条件下暂无内容
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              试试放宽时间范围，或切换到“最新发布”排序，看看更多相关爆料。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </div>
    </Layout>
  );
}